/**
 * useOrgCurrency Hook - Get organization currency from localStorage
 *
 * USAGE:
 * const currency = useOrgCurrency();
 *
 * Returns the organization's currency code (e.g., 'USD', 'LKR', 'EUR')
 * Falls back to 'USD' if no currency is set
 *
 * NOTE: Currency is set during login and stored in localStorage
 * This hook provides a centralized way to access it across components
 */

import { useState, useEffect } from 'react';

export const useOrgCurrency = (): string => {
  const [currency, setCurrency] = useState<string>(() => {
    return localStorage.getItem('currency') || 'USD';
  });

  useEffect(() => {
    // Listen for changes to localStorage (e.g., when user switches organizations)
    const handleStorageChange = () => {
      const updatedCurrency = localStorage.getItem('currency') || 'USD';
      setCurrency(updatedCurrency);
    };

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return currency;
};
